import { AlertCircle } from "lucide-react";
import { Fragment } from "react";

import type { ChatMessage } from "@/features/messaging/types/chat";
import { cn } from "@/utils/cn";
import { formatRelativeTime } from "@/utils/formatRelativeTime";

const URL_PATTERN = /(https?:\/\/[^\s]+)/g;

/** Splits on bare URLs (e.g. the Maps link `MessageComposer`'s location
 * button sends) and renders them as real links — plain text otherwise
 * would leave a pasted/shared URL uncopyable-by-tap and unclickable.
 * Checks each part with a plain prefix test, not `URL_PATTERN.test()` — a
 * global regex's `.test()` mutates its own `lastIndex` across calls, which
 * would silently skip every other URL in a message with more than one. */
function linkifyMessageText(text: string) {
  return text.split(URL_PATTERN).map((part, index) =>
    part.startsWith("http://") || part.startsWith("https://") ? (
      <a
        key={index}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="underline underline-offset-2 hover:no-underline"
      >
        {part}
      </a>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}

interface ChatBubbleProps {
  message: ChatMessage;
  isOwn: boolean;
  isGrouped?: boolean;
  onRetry?: () => void;
}

/** docs/ui/component-inventory.md's `ChatBubble`: sent (right, primary-
 * tinted), received (left, muted). `isGrouped` collapses vertical spacing
 * for consecutive same-sender messages. `timestamp` is a documented prop
 * of this component that had never actually been rendered — shown here as
 * a relative "Xm ago"-style caption under every bubble, not just the last
 * of a group, since nothing upstream tracks "is this the last message in
 * its run" (only "does this one continue the previous one"). */
export function ChatBubble({ message, isOwn, isGrouped, onRetry }: ChatBubbleProps) {
  const isFailed = message.status === "failed";
  const isSending = message.status === "sending";

  return (
    <div
      className={cn("flex", isOwn ? "justify-end" : "justify-start", isGrouped ? "mt-1" : "mt-3")}
    >
      <div className={cn("flex max-w-[75%] flex-col gap-1", isOwn && "items-end")}>
        <div
          className={cn(
            "rounded-2xl px-4 py-2 text-body",
            isOwn ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
            isSending && "opacity-60",
            isFailed && "border border-destructive bg-destructive/10 text-destructive",
          )}
        >
          <p className="whitespace-pre-line">{linkifyMessageText(message.text)}</p>
        </div>
        {!isSending && (
          <span className="px-1 text-caption text-muted-foreground">
            {formatRelativeTime(message.createdAt)}
          </span>
        )}
        {isFailed && (
          <button
            type="button"
            onClick={onRetry}
            className="flex items-center gap-1 text-caption text-destructive underline-offset-4 hover:underline"
          >
            <AlertCircle className="h-3 w-3" aria-hidden="true" />
            Failed to send. Retry
          </button>
        )}
      </div>
    </div>
  );
}
